import React from "react";
import { avatarColor, userInitials } from "../../Helper/avatar";

/** Locale number formatter shared across the dashboard widgets. */
export const numberFormatter = new Intl.NumberFormat();

// Avatar helpers live in one place now (src/Helper/avatar.ts). Re-exported here for existing imports;
// `avatarHue` is an alias of `avatarColor` : pass a stable account id (not the name) for per-account colour.
export { userInitials };
export const avatarHue = avatarColor;

/** Human-readable byte size (matches the Metrics page formatting). */
export const formatBytes = (value: number): string => {
	if (!Number.isFinite(value) || value <= 0) return "0 B";
	const units = ["B", "KB", "MB", "GB", "TB"];
	const magnitude = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
	const scaled = value / Math.pow(1024, magnitude);
	const digits = scaled >= 100 || magnitude === 0 ? 0 : scaled >= 10 ? 1 : 2;
	return `${scaled.toFixed(digits)} ${units[magnitude]}`;
};

/** A plain card: white surface, one gray hairline border, a readable title. No decoration. */
export const PanelCard: React.FC<{
	title: string;
	subtitle?: React.ReactNode;
	action?: React.ReactNode;
	className?: string;
	bodyClassName?: string;
	children: React.ReactNode;
}> = ({ title, subtitle, action, className, bodyClassName, children }) => (
	<section className={`flex min-w-0 flex-col rounded-xl border border-slate-200 bg-white ${className ?? ""}`}>
		<div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
			<div className="min-w-0">
				<h2 className="truncate text-sm font-semibold text-slate-800">{title}</h2>
				{subtitle && <p className="mt-0.5 truncate text-xs text-slate-400">{subtitle}</p>}
			</div>
			{action && <div className="shrink-0">{action}</div>}
		</div>
		<div className={`min-w-0 flex-1 ${bodyClassName ?? ""}`}>{children}</div>
	</section>
);

/** Delta pill (▲/▼ + magnitude + optional %), green up / rose down / slate flat. */
export const DeltaBadge: React.FC<{ delta: number | null; percent?: number | null; format?: (n: number) => string }> = ({
	delta,
	percent,
	format
}) => {
	if (delta === null) return null;
	const fmt = format ?? ((n: number) => numberFormatter.format(n));
	const tone = delta > 0 ? "bg-emerald-50 text-emerald-700" : delta < 0 ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-500";
	const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "●";
	return (
		<span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${tone}`}>
			{arrow} {fmt(Math.abs(delta))}
			{percent !== null && percent !== undefined && Number.isFinite(percent) && (
				<span className="opacity-70">
					({percent > 0 ? "+" : ""}
					{percent.toFixed(1)}%)
				</span>
			)}
		</span>
	);
};

/** Minimal inline SVG sparkline (area under a smooth-ish polyline). */
export const Sparkline: React.FC<{ data: number[]; color?: string; className?: string; height?: number }> = ({
	data,
	color = "#0095DB",
	className,
	height = 36
}) => {
	const points = (data ?? []).filter((n) => Number.isFinite(n));
	if (points.length < 2) {
		return <div className={`h-9 ${className ?? ""}`} aria-hidden />;
	}
	const width = 100;
	const min = Math.min(...points);
	const max = Math.max(...points);
	const span = max - min || 1;
	const step = width / (points.length - 1);
	const coords = points.map((value, index) => {
		const x = index * step;
		const y = height - ((value - min) / span) * (height - 4) - 2;
		return [x, y] as const;
	});
	const line = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
	const area = `${line} L${width},${height} L0,${height} Z`;
	const gradientId = `spark-${color.replace(/[^a-z0-9]/gi, "")}`;
	return (
		<svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className={`h-9 w-full ${className ?? ""}`}>
			<defs>
				<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0%" stopColor={color} stopOpacity="0.25" />
					<stop offset="100%" stopColor={color} stopOpacity="0" />
				</linearGradient>
			</defs>
			<path d={area} fill={`url(#${gradientId})`} />
			<path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
		</svg>
	);
};

/** A single point for {@link TrendChart}: a value plus optional axis labels and a hover title. */
export interface TrendPoint {
	value: number;
	/** Primary x-axis label under the point (e.g. day-of-month "12"). Omit to leave the point unlabelled. */
	label?: string;
	/** Secondary x-axis label, shown above the primary one (e.g. weekday "ven"). */
	sublabel?: string;
	/** Native hover tooltip for the point (e.g. "vendredi 12 juin · 3 visites"). */
	title?: string;
}

/**
 * Compact dated area chart. Unlike {@link Sparkline} it makes every point identifiable: a dot
 * sits on each value, an x-axis label is written under each labelled point, and a hover title
 * carries the exact value. The line + area are drawn in a stretched SVG (non-scaling stroke);
 * the dots and labels are percentage-positioned HTML so they stay round and aligned with the
 * curve at any width.
 */
export const TrendChart: React.FC<{
	points: TrendPoint[];
	color?: string;
	height?: number;
	className?: string;
}> = ({ points, color = "#0095DB", height = 64, className }) => {
	const valid = (points ?? []).filter((p) => p && Number.isFinite(p.value));
	if (valid.length < 2) {
		return <div style={{ height }} className={className} aria-hidden />;
	}
	const values = valid.map((p) => p.value);
	const min = Math.min(...values);
	const max = Math.max(...values);
	const span = max - min || 1;
	const padX = 6; // % horizontal inset so the edge dots/labels are not clipped
	const padY = 16; // % vertical headroom so the top/bottom dots stay inside the box
	const n = valid.length;
	const step = (100 - 2 * padX) / (n - 1);
	const geom = valid.map((p, i) => ({
		x: padX + i * step,
		y: padY + (1 - (p.value - min) / span) * (100 - 2 * padY),
		p,
	}));
	const line = geom.map((g, i) => `${i === 0 ? "M" : "L"}${g.x.toFixed(2)},${g.y.toFixed(2)}`).join(" ");
	const area = `${line} L${geom[n - 1].x.toFixed(2)},100 L${geom[0].x.toFixed(2)},100 Z`;
	const gid = `trend-${color.replace(/[^a-z0-9]/gi, "")}`;
	const hasLabels = geom.some((g) => g.p.label || g.p.sublabel);
	return (
		<div className={className}>
			<div className="relative" style={{ height }}>
				<svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible">
					<defs>
						<linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={color} stopOpacity="0.22" />
							<stop offset="100%" stopColor={color} stopOpacity="0" />
						</linearGradient>
					</defs>
					<path d={area} fill={`url(#${gid})`} />
					<path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
				</svg>
				{geom.map((g, i) => (
					<div
						key={i}
						className="group absolute top-0 z-10 h-full -translate-x-1/2"
						style={{ left: `${g.x}%`, width: `${step}%` }}
						title={g.p.title}
					>
						<span className="absolute left-1/2 top-0 hidden h-full w-px -translate-x-1/2 bg-slate-200 group-hover:block" aria-hidden />
						<span
							className="absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-sm transition-transform group-hover:scale-150"
							style={{ left: "50%", top: `${g.y}%`, backgroundColor: color }}
							aria-hidden
						/>
					</div>
				))}
			</div>
			{hasLabels && (
				<div className="relative mt-1.5 h-7">
					{geom.map((g, i) =>
						g.p.label || g.p.sublabel ? (
							<div key={i} className="absolute -translate-x-1/2 text-center leading-tight" style={{ left: `${g.x}%` }}>
								{g.p.sublabel && <div className="text-[9px] font-medium uppercase tracking-wide text-slate-300">{g.p.sublabel}</div>}
								{g.p.label && <div className="text-[11px] font-semibold tabular-nums text-slate-500">{g.p.label}</div>}
							</div>
						) : null
					)}
				</div>
			)}
		</div>
	);
};

/** Severity → light token styling for status chips/dots (0 down … 3 unknown). */
export const severityStyle = (severity: number) => {
	switch (severity) {
		case 0:
			return { dot: "bg-rose-500", text: "text-rose-700", chip: "border-rose-200 bg-rose-50 text-rose-700" };
		case 1:
			return { dot: "bg-amber-500", text: "text-amber-700", chip: "border-amber-200 bg-amber-50 text-amber-700" };
		case 2:
			return { dot: "bg-emerald-500", text: "text-emerald-700", chip: "border-emerald-200 bg-emerald-50 text-emerald-700" };
		default:
			return { dot: "bg-slate-300", text: "text-slate-500", chip: "border-slate-200 bg-slate-50 text-slate-500" };
	}
};
